import { LogOut, Sun, Moon } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuthStore } from '@/stores/authStore';
import type { NavItem } from '@/types/navigation';

interface AuthShellProps<V extends string> {
  navItems: NavItem<V>[];
  currentView: V;
  onViewChange: (view: V) => void;
  children: React.ReactNode;
  brandLetter?: string;
}

export function AuthShell<V extends string>({
  navItems,
  currentView,
  onViewChange,
  children,
  brandLetter = 'K',
}: AuthShellProps<V>) {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);

  let lastSection: string | undefined;

  return (
    <div className="bg-background text-foreground flex h-screen overflow-hidden">
      <aside className="bg-muted border-border/50 w-16 border-r">
        <div className="flex h-full flex-col">
          {/* Brand */}
          <div className="border-border flex h-12 items-center justify-center border-b">
            <span className="text-primary text-lg font-bold">{brandLetter}</span>
          </div>

          {/* Nav */}
          <nav className="flex-1 py-2">
            {navItems.map((item, idx) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              const showDivider = lastSection !== undefined && item.section !== lastSection;
              lastSection = item.section;

              return (
                <div key={item.id}>
                  {showDivider && (
                    <div className="border-border mx-3 my-1 border-t" />
                  )}
                  <button
                    onClick={() => onViewChange(item.id)}
                    title={item.label}
                    className={cn(
                      'group relative flex h-12 w-full items-center justify-center transition-colors',
                      !isActive && 'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                      isActive && 'text-primary bg-primary/10',
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {isActive && (
                      <div className="bg-primary absolute left-0 top-1/2 h-6 w-0.5 -translate-y-1/2 rounded-r" />
                    )}
                    <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                      {item.label}
                    </div>
                  </button>
                </div>
              );
            })}
          </nav>

          {/* Footer: user, theme, logout */}
          <div className="border-border space-y-1 border-t py-2">
            {user && (
              <div className="flex h-8 items-center justify-center">
                <span className="text-muted-foreground truncate text-[10px]">{user.username}</span>
              </div>
            )}
            <ThemeButton />
            <button
              onClick={() => logout()}
              title="Sign out"
              className={cn(
                'group relative flex h-12 w-full items-center justify-center transition-colors',
                'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
              )}
            >
              <LogOut className="h-5 w-5" />
              <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
                Sign out
              </div>
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}

function ThemeButton() {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <button
      onClick={toggleTheme}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className={cn(
        'group relative flex h-12 w-full items-center justify-center transition-colors',
        'text-muted-foreground hover:text-foreground hover:bg-muted/50',
      )}
    >
      {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      <div className="bg-popover text-popover-foreground invisible absolute left-full z-50 ml-2 whitespace-nowrap rounded px-2 py-1 text-xs opacity-0 shadow-lg transition-all group-hover:visible group-hover:opacity-100">
        {isDark ? 'Light mode' : 'Dark mode'}
      </div>
    </button>
  );
}
